import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import {
  CondutaEnum,
  DesfechoAtendimentoInput,
  FinalizacaoAtendimentoInput,
  ProcedimentoAtendimentoInput,
  RacionalidadeEmSaudeEnum,
  TipoEncaminhamentoOdontoEnum,
  TipoParticipacaoAtendimentoEnum,
} from 'graphql/types.generated'
import { compact, concat } from 'lodash'
import { isUndefinedOrNull } from 'util/checks'
import { v4 as uuidv4 } from 'uuid'
import { convertNovoAtendimentoModelToInput } from 'view/atendimentos/converter'

import { convertAgendarConsultas } from './components/agendar-consulta/converter-agendarConsultaAtendimento'
import { DesfechoAtendimentoFormModel, ManterCidadaoLista } from './components/DesfechoAtendimentoPanel'
import { NotificacaoCasoSuspeitoFieldModel } from './components/NotificacaoCasoSuspeitoField'
import { ProcedimentoSigtapFieldModel } from './components/ProcedimentoSigtapField'
import { FinalizacaoAtendimentoFormModel } from './FinalizacaoAtendimentoForm'
import {
  FinalizacaoAtendimentoQueryModel,
  NotificacaoCasoSuspeitoQueryModel,
  ProcedimentoAtendimentoQueryModel,
} from './model-finalizacao'

export const convertFinalizacaoFormToInput = (form: FinalizacaoAtendimentoFormModel): FinalizacaoAtendimentoInput => {
  return (
    form && {
      tipoAtendimento: form.tipoAtendimento,
      atendimentoCompartilhadoLotacaoId: form.atendimentoCompartilhadoLotacao?.id,
      procedimentosAdministrativos: convertProcedimentosAdministrativosFormToInput(form.procedimentosAdministrativos),
      fichaNotificacaoCasoSuspeito: form.fichasNotificacaoCasoSuspeito?.map((ficha) => ficha.fichaNotificacao.id),
      racionalidadeEmSaude: form.racionalidadeEmSaude as RacionalidadeEmSaudeEnum,
      condutas: convertCondutasFormToInput(form.conduta),
      condutasOdonto: convertCondutasOdontoFormToInput(form.condutasOdonto),
      fornecimentosOdonto: form.fornecimento,
      tipoConsultaOdonto: form.tipoConsulta,
      desfechoAtendimento: convertDesfechoAtendimentoFormToInput(form.desfechoAtendimento),
      agendamentoConsultas: convertAgendarConsultas(form.agendamentoConsultas),
      tipoParticipacaoCidadao: convertTipoParticipacaoCidadao(form.cidadaoPresente, form.tipoParticipacaoCidadao),
      tipoParticipacaoProfissionalConvidado: convertTipoParticipacaoProfissionalConvidado(
        form.atendimentoCompartilhadoLotacao,
        form.tipoParticipacaoProfissionalConvidado
      ),
    }
  )
}

const convertCondutasFormToInput = (condutas: CondutaEnum[]) => {
  return condutas?.filter((conduta) => conduta !== CondutaEnum.AGENDAMENTO_PARA_NASF)
}

const convertCondutasOdontoFormToInput = (condutasOdonto: TipoEncaminhamentoOdontoEnum[]) => {
  return condutasOdonto?.filter((condutaOdonto) => condutaOdonto !== TipoEncaminhamentoOdontoEnum.AGENDAMENTO_P_NASF)
}

export const convertFinalizacaoQueryToForm = (
  prevValue: FinalizacaoAtendimentoFormModel,
  finalizacao: FinalizacaoAtendimentoQueryModel
): FinalizacaoAtendimentoFormModel => {
  return {
    ...prevValue,
    tipoAtendimento: finalizacao.tipoAtendimento || prevValue.tipoAtendimento,
    conduta: compact(concat(prevValue?.conduta, finalizacao?.condutas)).removeDuplicate(),
    racionalidadeEmSaude: finalizacao.racionalidadeSaude,
    atendimentoCompartilhadoLotacao: finalizacao.lotacaoAtendimentoCompartilhado,
    fichasNotificacaoCasoSuspeito: convertFichasNotificacaoCasoSuspeitoQueryToForm(
      finalizacao.notificacoesCasoSuspeito
    ),
    procedimentosAdministrativos: convertProcedimentosAdmistrativosQueryToForm(
      prevValue?.procedimentosAdministrativos,
      finalizacao?.procedimentosAtendimento
    ),
  }
}

const convertDesfechoAtendimentoFormToInput = (
  desfechoAtendimento: DesfechoAtendimentoFormModel
): DesfechoAtendimentoInput => {
  if (!desfechoAtendimento) {
    return {
      manterCidadaoLista: false,
      atendimento: null,
    }
  }

  const manterCidadaoLista = desfechoAtendimento.manterCidadaoLista === ManterCidadaoLista.SIM

  return {
    manterCidadaoLista: manterCidadaoLista,
    atendimento: manterCidadaoLista ? convertNovoAtendimentoModelToInput(desfechoAtendimento.atendimento) : undefined,
  }
}

const convertFichasNotificacaoCasoSuspeitoQueryToForm = (
  items: NotificacaoCasoSuspeitoQueryModel[]
): NotificacaoCasoSuspeitoFieldModel[] => {
  return items?.map(({ id, notificacao }) => ({
    _id: uuidv4(),
    editingId: id,
    fichaNotificacao: notificacao,
  }))
}

export const convertFichasNotificacaoCasoSuspeitoQueryRetificacaoToForm = (
  items: NotificacaoCasoSuspeitoQueryModel[]
): NotificacaoCasoSuspeitoFieldModel[] =>
  items?.map(({ notificacao }) => ({
    _id: uuidv4(),
    fichaNotificacao: notificacao,
  }))

const convertProcedimentosAdmistrativosQueryToForm = (
  prevValue: ProcedimentoSigtapFieldModel[],
  items: ProcedimentoAtendimentoQueryModel[]
): ProcedimentoSigtapFieldModel[] => {
  return [
    ...(prevValue ?? []),
    ...(items?.map((item) => convertProcedimentoAdminstrativoQueryToForm(item)) ?? []),
  ].removeDuplicate((item) => item.procedimento.id)
}

export const convertProcedimentosAdmistrativosQueryRetificacaoToForm = (
  items: ProcedimentoAtendimentoQueryModel[]
): ProcedimentoSigtapFieldModel[] =>
  items
    ?.map((item) => convertProcedimentoAdminstrativoQueryRetificacaoToForm(item))
    .removeDuplicate((item) => item.procedimento?.id)

const convertProcedimentoAdminstrativoQueryToForm = ({
  id,
  procedimento,
  automatico,
}: ProcedimentoAtendimentoQueryModel): ProcedimentoSigtapFieldModel => {
  return {
    _id: uuidv4(),
    editingId: id,
    procedimento,
    automatico: automatico,
  }
}

export const convertProcedimentoAdminstrativoQueryRetificacaoToForm = ({
  procedimento,
  automatico,
}: ProcedimentoAtendimentoQueryModel): ProcedimentoSigtapFieldModel => ({
  _id: uuidv4(),
  procedimento,
  automatico: automatico,
})

const convertProcedimentosAdministrativosFormToInput = (
  items: ProcedimentoSigtapFieldModel[]
): ProcedimentoAtendimentoInput[] => {
  return items?.map((procedimento) => ({
    id: procedimento.procedimento.id,
    cid10PrincipalId: procedimento.procedimento.procedimentoReferencia?.id,
    automatico: procedimento.automatico,
  }))
}

const convertTipoParticipacaoCidadao = (
  cidadaoPresente: boolean,
  tipoParticipacaoCidadao: TipoParticipacaoAtendimentoEnum
): TipoParticipacaoAtendimentoEnum =>
  isUndefinedOrNull(cidadaoPresente)
    ? TipoParticipacaoAtendimentoEnum.PRESENCIAL
    : cidadaoPresente
    ? tipoParticipacaoCidadao ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
    : TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU

const convertTipoParticipacaoProfissionalConvidado = (
  profConvidado: LotacaoAndEstagioSelectFieldModel,
  tipoParticipacaoProfConvidado: TipoParticipacaoAtendimentoEnum
): TipoParticipacaoAtendimentoEnum =>
  profConvidado
    ? tipoParticipacaoProfConvidado ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
    : TipoParticipacaoAtendimentoEnum.NAO_PARTICIPOU
